/**
 * A key/value store in IndexedDB for what the app keeps on the device to work
 * offline. IndexedDB rather than localStorage: saved query data can run past
 * localStorage's ~5 MB.
 */
const DB_NAME = 'insafe-offline';
const STORE_NAME = 'kv';

function openDatabase(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const request = indexedDB.open(DB_NAME, 1);
    request.onupgradeneeded = () => {
      request.result.createObjectStore(STORE_NAME);
    };
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error);
  });
}

async function runRequest<T>(
  mode: IDBTransactionMode,
  makeRequest: (store: IDBObjectStore) => IDBRequest<T>,
): Promise<T> {
  const database = await openDatabase();
  try {
    return await new Promise<T>((resolve, reject) => {
      const request = makeRequest(
        database.transaction(STORE_NAME, mode).objectStore(STORE_NAME),
      );
      request.onsuccess = () => resolve(request.result);
      request.onerror = () => reject(request.error);
    });
  } finally {
    // Closed after every request so deleteOfflineStore is never blocked.
    database.close();
  }
}

export function readOfflineValue<T>(key: string): Promise<T | undefined> {
  return runRequest<T | undefined>('readonly', (store) => store.get(key));
}

export async function writeOfflineValue(
  key: string,
  value: unknown,
): Promise<void> {
  await runRequest('readwrite', (store) => store.put(value, key));
}

/** Deletes the whole store. Resolves whether or not it existed. */
export function deleteOfflineStore(): Promise<void> {
  return new Promise((resolve) => {
    if (typeof indexedDB === 'undefined') {
      resolve();
      return;
    }
    const request = indexedDB.deleteDatabase(DB_NAME);
    request.onsuccess = () => resolve();
    request.onerror = () => resolve();
    request.onblocked = () => resolve();
  });
}
